import { Component, type ReactNode } from 'react'

const CLAVE_INTENTO = 'novaberry-recargo-por-actualizacion'

/** Cuando se publica una versión nueva, los archivos JavaScript de la
 *  versión anterior dejan de existir en el servidor. Si alguien tenía
 *  la app abierta —o su navegador guardó una página vieja— el intento
 *  de cargar una pantalla nueva (una ruta que aún no había visitado)
 *  falla con un error de "módulo no encontrado" o, con el bundle ya a
 *  medio mezclar entre versiones, con errores más genéricos como
 *  "x is not a function": código de una versión llamando algo que en
 *  la otra ya no existe o cambió de forma. Sin este control React se
 *  queda en una pantalla en blanco sin ningún aviso.
 *
 *  La solución no es evitar el error: es reconocerlo y recargar una
 *  sola vez. Un `sessionStorage` evita el bucle infinito si el problema
 *  fuera otro. */
function esErrorDeVersionVieja(error: unknown) {
  const msj = error instanceof Error ? error.message : String(error)
  return /dynamically imported module|Failed to fetch dynamically|Importing a module script failed|ChunkLoadError|is not a function|is not defined|Unexpected token/i.test(
    msj,
  )
}

/** Borra todo lo que el navegador guardó de NovaBerry en este
 *  dispositivo: caché, service worker y datos locales. Sirve para el
 *  caso más difícil de explicar por chat —un dato viejo guardado en el
 *  navegador con una forma que el código actual ya no reconoce— sin
 *  pedirle a nadie que abra las herramientas de desarrollador. */
/** Purga solo lo que sirve archivos viejos —service worker y cachés—
 *  sin tocar la sesión ni nada guardado. Es la reparación automática:
 *  un service worker atascado sigue entregando archivos de una versión
 *  anterior mezclados con los nuevos, y eso revienta con errores del
 *  tipo "x is not a function" que no se arreglan recargando, porque al
 *  recargar el mismo service worker vuelve a entregar lo mismo. */
async function purgarServiceWorker() {
  try {
    if ('serviceWorker' in navigator) {
      const registros = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registros.map((r) => r.unregister()))
    }
    if ('caches' in window) {
      const claves = await caches.keys()
      await Promise.all(claves.map((k) => caches.delete(k)))
    }
  } catch {
    // Si el navegador no deja limpiar, igual conviene recargar: puede
    // que el problema fuera pasajero.
  }
}

async function borrarDatosGuardados() {
  try {
    localStorage.clear()
    sessionStorage.clear()
    if ('serviceWorker' in navigator) {
      const registros = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registros.map((r) => r.unregister()))
    }
    if ('caches' in window) {
      const claves = await caches.keys()
      await Promise.all(claves.map((k) => caches.delete(k)))
    }
  } finally {
    window.location.href = '/'
  }
}

interface Estado {
  fallo: boolean
  recuperable: boolean
  detalle: string
}

export class RecuperarActualizacion extends Component<{ children: ReactNode }, Estado> {
  state: Estado = { fallo: false, recuperable: true, detalle: '' }

  static getDerivedStateFromError(error: unknown): Estado {
    // Si ya se intentó recargar solo una vez en este mismo arranque y
    // el error volvió a aparecer, no es un problema de caché que se
    // arregle recargando de nuevo —hay que mostrar la pantalla con los
    // botones en vez de dejar la recarga automática en blanco para
    // siempre.
    const intentos = Number(sessionStorage.getItem(CLAVE_INTENTO) ?? 0)
    return {
      fallo: true,
      recuperable: esErrorDeVersionVieja(error) && intentos < 2,
      // El mensaje técnico se muestra igual: en un error que no
      // reconocemos, es mejor información concreta que una pantalla
      // muda — así se puede diagnosticar sin depender de que alguien
      // describa "algo salió mal" de memoria.
      // El rastro de componentes dice quién se estaba dibujando, pero
      // no dónde reventó: para eso hace falta la pila del error mismo.
      detalle:
        error instanceof Error
          ? `${error.message}\n${(error.stack ?? '').split('\n').slice(1, 5).join('\n')}`
          : String(error),
    }
  }

  componentDidCatch(error: unknown, info: { componentStack?: string | null }) {
    // Saber QUÉ componente falló es lo único que permite arreglar un
    // error que solo aparece en el teléfono de otra persona: el mensaje
    // suelto ("x is not a function") no dice dónde.
    const donde = info?.componentStack?.trim().split('\n').slice(0, 9).join(' ← ')
    if (donde) this.setState((s) => ({ ...s, detalle: `${s.detalle}\n\nEn: ${donde}` }))
    if (!esErrorDeVersionVieja(error)) return
    const intentos = Number(sessionStorage.getItem(CLAVE_INTENTO) ?? 0)

    // Primer intento: recargar sin más. Cubre el caso simple de haber
    // quedado con la pestaña abierta cuando salió una versión nueva.
    if (intentos === 0) {
      sessionStorage.setItem(CLAVE_INTENTO, '1')
      window.location.reload()
      return
    }

    // Segundo intento: si recargar no bastó, el service worker quedó
    // atascado sirviendo archivos viejos. Se desregistra y se borran
    // sus cachés —la sesión y los datos no se tocan— y se recarga.
    if (intentos === 1) {
      sessionStorage.setItem(CLAVE_INTENTO, '2')
      void purgarServiceWorker().then(() => window.location.reload())
      return
    }

    // Tercer fallo: ya se probó todo lo automático, toca mostrar la
    // pantalla con los botones en vez de seguir recargando en círculos.
  }

  render() {
    if (this.state.fallo && !this.state.recuperable) {
      return (
        <div className="grid min-h-dvh place-items-center superficie p-6">
          <div className="max-w-sm text-center">
            <h1 className="text-xl font-bold texto-base">Algo salió mal</h1>
            <p className="mt-2 text-sm texto-suave">
              Prueba primero con recargar. Si sigue igual, es casi seguro un dato guardado en
              este navegador que quedó desactualizado — el segundo botón lo limpia sin borrar
              nada de la base de datos.
            </p>
            {this.state.detalle && (
              <p className="codigo mt-4 rounded-2xl superficie-hundida px-4 py-3 text-left text-[12px] texto-tenue break-words">
                {this.state.detalle}
              </p>
            )}
            <div className="mt-6 flex flex-col gap-2.5">
              <button
                onClick={() => window.location.reload()}
                className="h-12 rounded-2xl bg-bosque-500 px-6 font-semibold text-white transition-colors hover:bg-bosque-600"
              >
                Recargar
              </button>
              <button
                onClick={() => void borrarDatosGuardados()}
                className="h-12 rounded-2xl border borde-base px-6 font-semibold texto-base transition-colors hover:superficie-hundida"
              >
                Borrar datos guardados en este dispositivo
              </button>
            </div>
          </div>
        </div>
      )
    }
    // Mientras se resuelve la recarga automática no se muestra nada
    // roto: la pantalla sigue en blanco un instante, pero recupera sola.
    if (this.state.fallo) return null
    return this.props.children
  }
}
